import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";

export type EducationSubject =
  | "math"
  | "history"
  | "reading"
  | "science";

type GradeBand =
  | "K-2"
  | "3-5"
  | "6-8"
  | "9-12";

type EducationAction =
  | "explain"
  | "hint"
  | "practice";

type Props = {
  subject: EducationSubject;
  title: string;
  description: string;
  placeholder: string;
};

const subjectNames: Record<
  EducationSubject,
  string
> = {
  math: "Math",
  history: "History",
  reading: "Reading",
  science: "Science",
};

function getToken(): string {
  const keys = [
    "ava_session_token",
    "ava_token",
    "token",
    "access_token",
    "authToken",
    "jwt",
  ];

  for (const key of keys) {
    const value =
      localStorage.getItem(key) || "";

    if (value.trim()) {
      return value.trim();
    }
  }

  return "";
}

function getUserId(): string {
  return (
    localStorage.getItem("user_id") ||
    localStorage.getItem("ava_user_id") ||
    "guest"
  );
}

export default function EducationClassroom({
  subject,
  title,
  description,
  placeholder,
}: Props) {
  const navigate = useNavigate();
  const { themeColors } = useContext(ThemeContext);

  const [grade, setGrade] =
    useState<GradeBand>("3-5");

  const [question, setQuestion] =
    useState("");

  const [busy, setBusy] =
    useState(false);

  const [response, setResponse] =
    useState(
      `Hello. I’m Ava. Welcome to ${
        subjectNames[subject]
      }. Ask me a ${
        subjectNames[subject]
      } question and we’ll work through it together.`
    );

  async function askAva(
    action: EducationAction
  ) {
    const clean = question.trim();

    if (!clean) {
      setResponse(
        `Please type a ${
          subjectNames[subject]
        } question first.`
      );
      return;
    }

    setBusy(true);

    try {
      const token = getToken();

      const res = await fetch(
        "https://api.avaaio.com/api/v1/chat",
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type":
              "application/json",
            "X-User-Timezone":
              Intl.DateTimeFormat()
                .resolvedOptions()
                .timeZone || "UTC",
            ...(token
              ? {
                  Authorization:
                    `Bearer ${token}`,
                }
              : {}),
          },
          body: JSON.stringify({
            message: clean,
            user_id: getUserId(),

            education_mode: true,
            education_subject: subject,
            education_grade: grade,
            education_action: action,

            avoid_repetition: true,

            recent_messages: [],

            client_time:
              new Date().toISOString(),

            client_local_time:
              new Date().toLocaleString(
                "en-US",
                { hour12: true }
              ),

            user_timezone:
              Intl.DateTimeFormat()
                .resolvedOptions()
                .timeZone || "UTC",

            timezone_offset_minutes:
              new Date()
                .getTimezoneOffset(),
          }),
        }
      );

      let data: Record<
        string,
        unknown
      > = {};

      try {
        data = await res.json();
      } catch {
        data = {};
      }

      if (!res.ok) {
        setResponse(
          String(
            data.detail ||
            data.error ||
            "Ava could not answer that classroom question right now."
          )
        );
        return;
      }

      setResponse(
        String(
          data.response ||
          data.reply ||
          data.text ||
          "Ava did not return a classroom response."
        )
      );
    } catch {
      setResponse(
        "I could not reach Ava right now. Please try again."
      );
    } finally {
      setBusy(false);
    }
  }

  function clearAll() {
    setQuestion("");

    setResponse(
      `Ask me another ${
        subjectNames[subject]
      } question when you're ready.`
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
        boxSizing: "border-box",
        padding: "16px 12px",
        color: themeColors.text,
        background: themeColors.bg,
      }}
    >
      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
        }}
      >
        <div
          style={{
            borderRadius: "20px",
            padding: "16px",
            marginBottom: "14px",
            background:
              themeColors.panel,
            border:
              `1px solid ${themeColors.border}`,
          }}
        >
          <div
            style={{
              fontSize: "11px",
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: themeColors.accent2,
              fontWeight: 800,
            }}
          >
            Ava Education Classroom
          </div>

          <h1
            style={{
              margin: "6px 0",
              color: themeColors.accent,
              fontSize:
                "clamp(26px,4vw,46px)",
            }}
          >
            {title}
          </h1>

          <p
            style={{
              margin: 0,
              color:
                themeColors.mutedText,
              lineHeight: 1.5,
            }}
          >
            {description}
          </p>
        </div>

        <div
          className="avaaio-classroom-grid"
          style={{
            display: "grid",
            gridTemplateColumns:
              "minmax(0,1.4fr) minmax(300px,0.9fr)",
            gap: "14px",
          }}
        >
          <div
            style={{
              padding: "16px",
              borderRadius: "18px",
              background:
                themeColors.cardBg,
              border:
                `1px solid ${themeColors.border}`,
            }}
          >
            <div
              style={{
                fontWeight: 800,
                marginBottom: "6px",
              }}
            >
              Grade Level
            </div>

            <select
              value={grade}
              onChange={(e) =>
                setGrade(
                  e.target.value as GradeBand
                )
              }
              style={{
                width: "100%",
                minHeight: "44px",
                padding: "10px",
                marginBottom: "14px",
                color: themeColors.text,
                background: themeColors.panel,
                border:
                  `1px solid ${themeColors.border}`,
                borderRadius: "12px",
              }}
            >
              <option value="K-2">
                K–2
              </option>
              <option value="3-5">
                3–5
              </option>
              <option value="6-8">
                6–8
              </option>
              <option value="9-12">
                9–12
              </option>
            </select>

            <div
              style={{
                fontWeight: 800,
                marginBottom: "6px",
              }}
            >
              Student Question
            </div>

            <textarea
              value={question}
              onChange={(e) =>
                setQuestion(
                  e.target.value
                )
              }
              placeholder={placeholder}
              style={{
                width: "100%",
                minHeight: "130px",
                boxSizing: "border-box",
                resize: "vertical",
                padding: "12px",
                color: themeColors.text,
                background: themeColors.panel,
                border:
                  `1px solid ${themeColors.border}`,
                borderRadius: "12px",
                fontSize: "16px",
                fontFamily: "inherit",
              }}
            />

            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit,minmax(140px,1fr))",
                gap: "8px",
                marginTop: "12px",
              }}
            >
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void askAva("explain")
                }
              >
                Explain
              </button>

              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void askAva("hint")
                }
              >
                Give Hint
              </button>

              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void askAva("practice")
                }
              >
                Practice
              </button>

              <button
                type="button"
                disabled={busy}
                onClick={clearAll}
              >
                Clear
              </button>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gap: "12px",
              alignContent: "start",
            }}
          >
            <div
              style={{
                padding: "16px",
                borderRadius: "18px",
                background:
                  themeColors.cardBg,
                border:
                  `1px solid ${themeColors.border}`,
              }}
            >
              <div
                style={{
                  fontWeight: 900,
                  marginBottom: "8px",
                }}
              >
                Ava Response
              </div>

              <div
                style={{
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.6,
                }}
              >
                {busy
                  ? "Ava is thinking…"
                  : response}
              </div>
            </div>

            <button
              type="button"
              onClick={() =>
                navigate("/education")
              }
            >
              Back to Education
            </button>
          </div>
        </div>

        <style>{`
          @media (max-width: 900px) {
            .avaaio-classroom-grid {
              grid-template-columns: 1fr !important;
            }
          }

          .avaaio-classroom-grid button {
            min-height: 44px;
            border-radius: 12px;
            border: 1px solid var(--ava-border);
            background: var(--ava-button);
            color: var(--ava-text);
            font-weight: 800;
            cursor: pointer;
            padding: 0 14px;
          }

          .avaaio-classroom-grid button:disabled {
            opacity: 0.55;
            cursor: default;
          }
        `}</style>
      </div>
    </div>
  );
}
